import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useMessageStore } from "@/state/stores";
import { Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import { nanoid } from "nanoid";
import { useCallback } from "react";
import styles from "./ResponseFeedback.module.css";

export function ResponseFeedback({
  messageId,
  chatId,
}: { messageId: string; chatId: string }) {
  const { toast } = useToast();

  const message = useMessageStore(
    useCallback(
      (state) => state.messages.find((msg) => msg.id === messageId),
      [messageId],
    ),
  );

  const extractCopyableContent = useCallback(() => {
    if (!message) {
      return "";
    }

    // For parts-based messages
    if (message.usesParts && message.parts) {
      return message.parts
        .filter((part) => part.type === "text")
        .map((part) => part.content)
        .join("\n");
    }

    // For legacy messages
    return message.content || "";
  }, [message]);

  const handleCopy = useCallback(async () => {
    const content = extractCopyableContent();
    if (!content) {
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      toast({
        duration: 2000,
        title: "Copied!",
        description: "Message content copied to clipboard",
      });
    } catch (error) {
      console.error("Failed to copy content:", error);
      toast({
        duration: 2000,
        title: "Copy failed",
        description: "Unable to copy content to clipboard",
        variant: "destructive",
      });
    }
  }, [extractCopyableContent, toast]);

  return (
    <ul className={cn("flex gap-2", styles.feedback)}>
      <li>
        <Button variant="ghost" size="icon-xs" onClick={handleCopy}>
          <Copy />
        </Button>
      </li>
      <li>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => {
            toast({
              duration: 2500,
              title: "Feedback given",
              description: "Thank you for your feedback!",
            });

            api
              .giveThumbsUp({
                id: nanoid(),
                messageId,
                chatId,
              })
              .catch((error) => {
                console.error("Error recording thumbs up:", error);
              });
          }}
        >
          <ThumbsUp />
        </Button>
      </li>
      <li>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => {
            toast({
              duration: 2500,
              title: "Feedback given",
              description: "Thank you for your feedback!",
            });

            api
              .giveThumbsDown({
                id: nanoid(),
                messageId,
                chatId,
              })
              .catch((error) => {
                console.error("Error recording thumbs down:", error);
              });
          }}
        >
          <ThumbsDown />
        </Button>
      </li>
    </ul>
  );
}
